import { PlannerReadinessTable } from './PlannerReadinessTable';
import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, GitMerge, Loader2, RefreshCw, Scissors } from 'lucide-react';
import { api } from '@/utils/api';

export interface PlannerRow {
  id: string; productName: string; sku: string | null; soDemandQty: number; topUpQty: number; plannedQty: number; qtyOverridden: boolean;
  soLinks: Array<{ salesOrderNumber: string; qty: number }>; commitBy: string | null; commitInHours: number | null; lineLoadPct: number | null;
  material: { status: 'ok' | 'short' | 'no_bom'; shortages: Array<{ name: string; needed: number; available: number }> }; blockReason: string | null;
}

const dayMonth = (iso: string) => new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', timeZone: 'Asia/Kolkata' });
const commitText = (r: PlannerRow) => (r.commitInHours !== null && r.commitInHours >= 0 && r.commitInHours < 48 ? `${r.commitInHours} h left` : r.commitBy ? dayMonth(r.commitBy) : '—');
const TH = 'px-3 py-2 text-left text-xs font-medium text-gray-500';

export function PlannerQueuePanel({readiness = false}: {readiness?: boolean}) {
  const [rows, setRows] = useState<PlannerRow[]>([]);
  const [flags, setFlags] = useState({ capacityLoaded: true, maxLevelsLoaded: true });
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | undefined>();
  const [notice, setNotice] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await api.get('/manufacturing/planner/queue');
      setRows(res.data.data.rows);
      setFlags({ capacityLoaded: res.data.data.capacityLoaded, maxLevelsLoaded: res.data.data.maxLevelsLoaded });
      setDrafts({});
    } catch (err: any) {
      setRows([]);
      setErrorCode(err.response?.data?.code);
      setError(err.response?.data?.error || 'Failed to load the planner queue');
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (key: string, fn: () => Promise<any>, onOk?: (res: any) => void) => {
    setBusy(key); setError(null); setNotice(null);
    try { const res = await fn(); onOk?.(res); await load(); }
    catch (err: any) { setErrorCode(err.response?.data?.code);
      setError(err.response?.data?.error || 'That did not work.'); }
    finally { setBusy(null); }
  };

  const saveQty = (r: PlannerRow) => {
    const raw = drafts[r.id];
    if (raw === undefined || Number(raw) === r.plannedQty) return;
    act(`qty-${r.id}`, () => api.patch(`/manufacturing/planner/lines/${r.id}`, { plannedQty: Number(raw) }));
  };
  const release = (r: PlannerRow) => act(`rel-${r.id}`, () => api.post(`/manufacturing/planner/lines/${r.id}/release`), (res) => {
    const { productionOrder, serials } = res.data.data;
    setNotice(`${productionOrder.number} released with serials ${serials[0]} … ${serials[serials.length - 1]}.`);
  });
  const split = (r: PlannerRow) => {
    const qty = window.prompt(`Move how many of ${r.plannedQty} to a new line?`);
    if (qty) act(`split-${r.id}`, () => api.post(`/manufacturing/planner/lines/${r.id}/split`, { qty: Number(qty) }));
  };
  const merge = () => act('merge', () => api.post('/manufacturing/planner/merge', { lineIds: [...selected] }), () => setSelected(new Set()));

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="animate-spin text-gray-400" size={20} /></div>;

  if(readiness) return <PlannerReadinessTable {...{rows,error,notice,busy,release}} reload={load}/>;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-xs text-gray-500 space-y-0.5">
          <p>Release opens the work order and creates its serials. Engine proposes every 6 h; nothing releases itself.</p>
          {!flags.capacityLoaded && <p>Capacity is not loaded. Line load is not recorded.</p>}
          {!flags.maxLevelsLoaded && <p>Max levels are not loaded. No top-ups proposed.</p>}
        </div>
        <div className="flex gap-2">
          <button onClick={merge} disabled={selected.size < 2 || busy !== null} className="flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-40"><GitMerge size={14} />Merge ({selected.size})</button>
          <button onClick={load} className="flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50"><RefreshCw size={14} />Refresh</button>
        </div>
      </div>

      {error && <div role="alert" data-code={errorCode} className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"><AlertTriangle size={14} />{error}</div>}
      {notice && <div role="status" className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700"><CheckCircle2 size={14} />{notice}</div>}

      <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className={TH}>Order</th><th className={TH}>Product</th><th className={TH}>Orders</th><th className={TH}>Top-up to max</th><th className={TH}>Total</th>
              <th className={TH}>Commit by</th><th className={TH}>Line load</th><th className={TH}>Material</th><th className={TH}></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.length === 0 && <tr><td colSpan={9} className="px-3 py-10 text-center text-gray-500">Nothing to plan right now</td></tr>}
            {rows.map((r) => {
              const shortName = r.material.shortages[0]?.name;
              return (
                <tr key={r.id} className="hover:bg-gray-50">
                  <td className="px-3 py-2">{readiness ? `Plan ${r.id.slice(-8)}` : <input type="checkbox" aria-label={`Select ${r.productName}`} checked={selected.has(r.id)} onChange={(e) => setSelected((prev) => { const n = new Set(prev); if (e.target.checked) n.add(r.id); else n.delete(r.id); return n; })} />}</td>
                  <td className="px-3 py-2 font-medium text-slate-900">{r.productName}</td>
                  <td className="px-3 py-2 text-gray-600">{r.soDemandQty > 0 ? `${r.soDemandQty} · ${r.soLinks.map((l) => l.salesOrderNumber).join(', ')}` : '0'}</td>
                  <td className="px-3 py-2 text-gray-600">{r.topUpQty}</td>
                  <td className="px-3 py-2">
                    <input aria-label={`Total for ${r.productName}`} type="number" min={1} value={drafts[r.id] ?? String(r.plannedQty)}
                      onChange={(e) => setDrafts((d) => ({ ...d, [r.id]: e.target.value }))} onBlur={() => saveQty(r)}
                      className={`w-20 rounded-lg border px-2 py-1 text-sm ${r.qtyOverridden ? 'border-blue-300 bg-blue-50' : 'border-gray-200'}`} />
                  </td>
                  <td className={`px-3 py-2 ${r.commitInHours !== null && r.commitInHours < 48 ? 'font-medium text-amber-600' : 'text-gray-600'}`}>{commitText(r)}</td>
                  <td className="px-3 py-2 text-gray-600">{r.lineLoadPct === null ? '—' : `${r.lineLoadPct}%`}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${r.material.status === 'ok' ? 'bg-green-50 text-green-700' : r.material.status === 'short' ? 'bg-amber-50 text-amber-700' : 'bg-gray-100 text-gray-500'}`}>
                      {r.material.status === 'ok' ? 'ok' : r.material.status === 'short' ? `${shortName} short` : 'No BOM'}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1.5">
                      <button title="Split" aria-label="Split" onClick={() => split(r)} disabled={busy !== null || r.plannedQty < 2} className="rounded-lg border border-gray-200 p-1.5 text-gray-500 hover:bg-gray-50 disabled:opacity-40"><Scissors size={13} /></button>
                      <button onClick={() => release(r)} disabled={busy !== null || !!r.blockReason} title={r.blockReason ?? undefined}
                        className="rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed">
                        {busy === `rel-${r.id}` ? 'Releasing…' : 'Release'}
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
